import { DOCUMENT, Injectable, computed, effect, inject, signal } from '@angular/core';
import { Subject } from 'rxjs';

import { BRAND_PRIMARY, primaryColors, surfacePalettes } from '../constant/color-scheme.constant';
import { ILayoutConfig, ILayoutState, IMenuChangeEvent } from '../interfaces/layout.interface';
import { LOCAL_STORAGE_KEY } from '../../core/constant/local-storage.constant';
import { DARK_MODE_CLASS } from '../../theme/constant/theme.constant';

// Dark mode, the primary and surface colours, and the menu's open state.
@Injectable({ providedIn: 'root' })
export class LayoutService {
    private readonly document = inject(DOCUMENT);

    private readonly overlayOpen = new Subject<null>();
    private readonly menuSource = new Subject<IMenuChangeEvent>();

    readonly overlayOpen$ = this.overlayOpen.asObservable();
    readonly menuSource$ = this.menuSource.asObservable();

    readonly layoutConfig = signal<ILayoutConfig>(this.loadConfig());
    private readonly signInLook = signal(false);

    readonly layoutState = signal<ILayoutState>({
        staticMenuDesktopInactive: false,
        overlayMenuActive: false,
        staticMenuMobileActive: false,
        menuHoverActive: false,
    });

    readonly isDarkTheme = computed(() => this.layoutConfig().darkTheme);
    readonly theme = computed(() => (this.layoutConfig().darkTheme ? 'dark' : 'light'));
    readonly getPrimary = computed(() => this.layoutConfig().primary);
    readonly getSurface = computed(() => this.layoutConfig().surface);
    readonly isOverlay = computed(() => this.layoutConfig().menuMode === 'overlay');

    constructor() {
        effect(() => {
            const config = this.layoutConfig();
            const isSignIn = this.signInLook();
            this.document.documentElement.classList.toggle(
                DARK_MODE_CLASS,
                config.darkTheme && !isSignIn,
            );
            this.applyPrimaryColor(isSignIn ? BRAND_PRIMARY : config.primary);
            this.applySurfaceColor(config.surface);
            this.saveConfig(config);
        });
    }

    toggleDarkMode(): void {
        this.layoutConfig.update((prev) => ({ ...prev, darkTheme: !prev.darkTheme }));
    }

    setPrimaryColor(colorName: string): void {
        this.layoutConfig.update((prev) => ({ ...prev, primary: colorName }));
    }

    // The sign-in pages keep the brand colours and the light theme, whatever the admin panel
    // was set to.
    useSignInLook(isOn: boolean): void {
        this.signInLook.set(isOn);
    }

    setSurfaceColor(colorName: string | null): void {
        this.layoutConfig.update((prev) => ({ ...prev, surface: colorName }));
    }

    setMenuMode(mode: string): void {
        this.layoutConfig.update((prev) => ({ ...prev, menuMode: mode }));
    }

    toggleMenu(): void {
        const isDesktop = (this.document.defaultView?.innerWidth ?? 0) > 991;

        if (this.layoutConfig().menuMode === 'overlay') {
            this.layoutState.update((state) => ({
                ...state,
                overlayMenuActive: !state.overlayMenuActive,
            }));
            if (this.layoutState().overlayMenuActive) this.overlayOpen.next(null);
            return;
        }

        if (isDesktop) {
            this.layoutState.update((state) => ({
                ...state,
                staticMenuDesktopInactive: !state.staticMenuDesktopInactive,
            }));
            return;
        }

        this.layoutState.update((state) => ({
            ...state,
            staticMenuMobileActive: !state.staticMenuMobileActive,
        }));
        if (this.layoutState().staticMenuMobileActive) this.overlayOpen.next(null);
    }

    hideMenu(): void {
        this.layoutState.update((state) => ({
            ...state,
            overlayMenuActive: false,
            staticMenuMobileActive: false,
            menuHoverActive: false,
        }));
    }

    onMenuStateChange(event: IMenuChangeEvent): void {
        this.menuSource.next(event);
    }

    // Every shade, not only the main colour, so Tailwind's primary-* classes follow too.
    private applyPrimaryColor(colorName: string): void {
        const palette = primaryColors.find((c) => c.name === colorName)?.palette;
        if (!palette) return;

        Object.entries(palette).forEach(([shade, value]) => {
            if (typeof value === 'string') {
                this.document.documentElement.style.setProperty(`--p-primary-${shade}`, value);
            }
        });
    }

    private applySurfaceColor(colorName: string | null | undefined): void {
        if (!colorName) return;
        const palette = surfacePalettes[colorName];
        if (!palette) return;

        Object.entries(palette).forEach(([shade, value]) => {
            this.document.documentElement.style.setProperty(`--p-surface-${shade}`, value);
        });
    }

    private loadConfig(): ILayoutConfig {
        const defaults: ILayoutConfig = {
            preset: 'Aura',
            primary: BRAND_PRIMARY,
            surface: null,
            darkTheme:
                this.document.defaultView?.matchMedia('(prefers-color-scheme: dark)').matches ??
                false,
            menuMode: 'static',
        };

        const saved = this.document.defaultView?.localStorage.getItem(
            LOCAL_STORAGE_KEY.LAYOUT_CONFIG,
        );
        if (saved) {
            try {
                return { ...defaults, ...(JSON.parse(saved) as Partial<ILayoutConfig>) };
            } catch {
                // A half-written value must not stop the app booting.
            }
        }

        return defaults;
    }

    private saveConfig(config: ILayoutConfig): void {
        this.document.defaultView?.localStorage.setItem(
            LOCAL_STORAGE_KEY.LAYOUT_CONFIG,
            JSON.stringify(config),
        );
    }
}
